import React, { useState, useEffect, useMemo, useRef } from "react";
import { Toasts, useToasts } from "./../shared/Toast";
import { get } from "@rails/request.js";
import Loading from "./../shared/Loading";
import ProofRow from "./ProofRow";

const SORT_OPTIONS = [
  { value: "newest",       label: "Newest first"    },
  { value: "oldest",       label: "Oldest first"    },
  { value: "updated_desc", label: "Recently updated" },
  { value: "updated_asc",  label: "Least recently updated" },
];

const KIND_OPTIONS = ["all", "text", "video", "audio"];
// Moderation state. "pending" is the queue an owner works through after
// an import or a run of capform submissions.
const STATUS_OPTIONS = ["all", "approved", "pending"];

const VIEW_STORAGE_KEY = "shosay:proof-index-view";
const PAGE_SIZE = 50;

// The page lives in ?page= so a reload, a shared link, or coming back from
// a proof lands where you left off. Other params (capform_id scopes this
// list) have to survive the rewrite, so go through URLSearchParams rather
// than building the query by hand.
const pageFromUrl = () => {
  if (typeof window === "undefined") return 1;
  const n = parseInt(new URLSearchParams(window.location.search).get("page"), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const writePageToUrl = (n, { replace = false } = {}) => {
  const url = new URL(window.location.href);
  if (n <= 1) url.searchParams.delete("page");
  else url.searchParams.set("page", String(n));
  if (url.href === window.location.href) return;
  window.history[replace ? "replaceState" : "pushState"]({}, "", url);
};

const timeOrNull = (iso) => (iso ? new Date(iso).getTime() : 0);

const dateLabel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 1)  return "today";
  if (days < 2)  return "yesterday";
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
};

const truncate = (str, n) => {
  if (!str) return "";
  const clean = String(str).replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  return clean.length <= n ? clean : clean.slice(0, n).trimEnd() + "…";
};

const ProofIndex = ({ projectId, capformId = null, seeAllHref = null }) => {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toasts, setToasts, addToast } = useToasts();

  const [search, setSearch] = useState("");
  const [sort, setSort]     = useState("newest");
  const [kind, setKind]     = useState("all");
  const [source, setSource] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(pageFromUrl);

  // View mode (cards vs compact table) persists across visits — owners
  // who curate dozens of proofs at once typically pick the compact view
  // and don't want to re-toggle every time.
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "cards";
    return window.localStorage.getItem(VIEW_STORAGE_KEY) || "cards";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
    }
  }, [viewMode]);

  const fetchProofs = async () => {
    const scope = capformId ? `&capform_id=${capformId}` : "";
    const response = await get(`/api/v1/proofs?project_id=${projectId}${scope}`, {
      responseKind: "application/json",
    });
    if (response.ok) {
      const json = await response.json;
      setProofs(json.proofs);
    } else {
      addToast("error", "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchProofs();
  }, []);

  const sources = useMemo(() => {
    const s = new Set(proofs.map((p) => p.source_platform).filter(Boolean));
    return Array.from(s).sort();
  }, [proofs]);

  const filtered = useMemo(() => {
    let list = proofs.slice();

    if (kind !== "all")   list = list.filter((p) => p.kind === kind);
    if (source !== "all") list = list.filter((p) => p.source_platform === source);
    if (status !== "all") list = list.filter((p) => (status === "approved" ? p.approved : !p.approved));

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const haystack = [
          p.headline,
          p.body,
          p.name,
          p.email,
          p.contact_title,
          p.contact_company_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    list.sort((a, b) => {
      switch (sort) {
        case "oldest":       return timeOrNull(a.created_at_iso) - timeOrNull(b.created_at_iso);
        case "updated_desc": return timeOrNull(b.updated_at_iso) - timeOrNull(a.updated_at_iso);
        case "updated_asc":  return timeOrNull(a.updated_at_iso) - timeOrNull(b.updated_at_iso);
        case "newest":
        default:             return timeOrNull(b.created_at_iso) - timeOrNull(a.created_at_iso);
      }
    });
    return list;
  }, [proofs, search, sort, kind, source, status]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  // Narrowing the list invalidates the page number. Page 4 of 12 results
  // is an empty screen that reads as a broken list. Replace rather than
  // push so filtering doesn't fill the back button with dead pages.
  const filterKey = [ search, sort, kind, source, status ].join("\u0000");
  const firstFilterRun = useRef(true);
  useEffect(() => {
    if (firstFilterRun.current) { firstFilterRun.current = false; return; }
    setPage(1);
    writePageToUrl(1, { replace: true });
  }, [filterKey]);

  // Paging is a navigation, so it pushes; browser back/forward then has to
  // move the list with it or the URL and the screen disagree.
  const goToPage = (n) => {
    setPage(n);
    writePageToUrl(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const onPop = () => setPage(pageFromUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const resetFilters = () => {
    setSearch("");
    setSort("newest");
    setKind("all");
    setSource("all");
    setStatus("all");
  };

  const filtersActive = search || kind !== "all" || source !== "all" || status !== "all" || sort !== "newest";

  if (loading) {
    return (
      <div className="mt-10 flex w-full items-center justify-center mx-auto">
        <Loading />
      </div>
    );
  }

  if (proofs.length === 0) return null;

  return (
    <div className="w-full">
      <Toasts toasts={toasts} setToasts={setToasts} />

      <div className="zz-panel p-4 sm:p-5 mb-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <label className="relative flex-1">
              <span className="sr-only">Search</span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="search headline, body, name…"
                className="w-full border-[3px] border-black rounded-full px-4 py-2 text-sm font-medium focus:outline-none focus:ring-0 focus:border-ssblue placeholder-slate-400"
              />
            </label>
            <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
              <span className="zz-hand text-lg text-slate-500">sort by</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="border-[3px] border-black rounded-full px-3 py-2 text-sm font-bold bg-white focus:outline-none focus:border-ssblue cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="zz-hand text-lg text-slate-500 mr-1">kind</span>
              {KIND_OPTIONS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`zz-filter ${kind === k ? "is-active" : ""}`}
                >
                  {k}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="zz-hand text-lg text-slate-500 mr-1">status</span>
              {STATUS_OPTIONS.map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setStatus(st)}
                  className={`zz-filter ${status === st ? "is-active" : ""}`}
                >
                  {st}
                </button>
              ))}
            </div>
            {sources.length > 1 && (
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700 sm:ml-auto">
                <span className="zz-hand text-lg text-slate-500">source</span>
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="border-[3px] border-black rounded-full px-3 py-2 text-sm font-bold bg-white focus:outline-none focus:border-ssblue cursor-pointer"
                >
                  <option value="all">any</option>
                  {sources.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            )}
            {filtersActive && (
              <button
                type="button"
                onClick={resetFilters}
                className="zz-link text-xs sm:ml-auto"
              >
                clear filters ×
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                showing <span className="text-slate-900">{paged.length}</span>
                {filtered.length > PAGE_SIZE && <> of {filtered.length} matching</>}
                {" "}of {proofs.length}
              </p>
              {/* Only on a scoped list — the way back out to every proof
                  in the project, since the list is filtered to one form. */}
              {seeAllHref && (
                <a href={seeAllHref} className="zz-link text-xs">see all proofs →</a>
              )}
            </div>
            <div className="inline-flex items-center border-[2px] border-black rounded-full overflow-hidden text-[11px] font-bold uppercase tracking-wider">
              <button
                type="button"
                onClick={() => setViewMode("cards")}
                aria-pressed={viewMode === "cards"}
                className={`px-3 py-1.5 transition-colors ${viewMode === "cards" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode("compact")}
                aria-pressed={viewMode === "compact"}
                className={`px-3 py-1.5 border-l-[2px] border-black transition-colors ${viewMode === "compact" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                compact
              </button>
            </div>
          </div>
        </div>
      </div>

      {filtered.length > 0 ? (
        viewMode === "compact" ? (
          <div className="zz-panel p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b-[2px] border-black bg-slate-50">
                    <th className="py-2.5 px-3 font-black uppercase tracking-wider text-[10px]">kind</th>
                    <th className="py-2.5 px-3 font-black uppercase tracking-wider text-[10px]">contact</th>
                    <th className="py-2.5 px-3 font-black uppercase tracking-wider text-[10px]">preview</th>
                    <th className="py-2.5 px-3 font-black uppercase tracking-wider text-[10px]">added</th>
                    <th className="py-2.5 px-3 font-black uppercase tracking-wider text-[10px]">status</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((proof) => {
                    const preview = truncate(proof.headline || proof.body || proof.snippet, 90);
                    const manageUrl = `/projects/${projectId}/proofs/${proof.id}/manage`;
                    const open = (e) => {
                      if (e.metaKey || e.ctrlKey || e.button === 1) window.open(manageUrl, "_blank");
                      else window.location.href = manageUrl;
                    };
                    return (
                      <tr
                        key={proof.uuid}
                        onClick={open}
                        onAuxClick={(e) => { if (e.button === 1) open(e); }}
                        onKeyDown={(e) => { if (e.key === "Enter") open(e); }}
                        tabIndex={0}
                        role="link"
                        aria-label={`Manage proof from ${proof.name || "Anonymous"}`}
                        className="border-b border-slate-200 align-top cursor-pointer hover:bg-ssyellow/20 focus:bg-ssyellow/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ssblue">
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">
                            {proof.kind}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 min-w-[140px]">
                          <div className="font-bold text-slate-900 truncate">{proof.name || "Anonymous"}</div>
                          {(proof.contact_title || proof.contact_company_name) && (
                            <div className="text-[11px] text-slate-500 truncate">
                              {proof.contact_title}
                              {proof.contact_title && proof.contact_company_name ? " · " : ""}
                              {proof.contact_company_name}
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 max-w-[420px]">
                          <div className="text-slate-700 line-clamp-2 leading-snug">{preview || <span className="italic text-slate-400">—</span>}</div>
                        </td>
                        <td className="py-2.5 px-3 whitespace-nowrap text-slate-600 text-xs">
                          {dateLabel(proof.created_at_iso)}
                        </td>
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          {proof.approved ? (
                            <span className="text-[10px] font-black uppercase tracking-wider bg-ssgreen/20 text-ssgreen-dark px-2 py-0.5 rounded">approved</span>
                          ) : (
                            <span className="text-[10px] font-black uppercase tracking-wider bg-ssyellow/30 text-slate-800 px-2 py-0.5 rounded">pending</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            {paged.map((proof, index) => (
              <ProofRow key={proof.uuid} initProof={proof} index={index} />
            ))}
          </div>
        )
      ) : (
        <div className="zz-panel p-8 text-center max-w-lg mx-auto">
          <p className="zz-hand text-2xl text-ssblue">nothing here —</p>
          <p className="zz-display text-2xl font-black mt-1">No matches.</p>
          <p className="text-sm text-slate-600 mt-2">Try loosening your filters or clearing the search.</p>
          <button onClick={resetFilters} className="zz-btn sm blue mt-4">clear filters</button>
        </div>
      )}

      {pageCount > 1 && (
        <div className="flex items-center justify-center gap-3 flex-wrap mt-8">
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`zz-btn sm orange ${currentPage === 1 ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            ← previous
          </button>
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            page <span className="text-slate-900">{currentPage}</span> of {pageCount}
          </span>
          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === pageCount}
            className={`zz-btn sm orange ${currentPage === pageCount ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            next →
          </button>
        </div>
      )}
    </div>
  );
};

export default ProofIndex;
